import React, { useEffect } from 'react';
import { leaderboardAPI } from '../services/api';
import { useApiState } from '../hooks/useApiState';
import { useSortableTable } from '../hooks/useSortableTable';
import './RoundStandings.css';

const RoundStandings = ({
  currentRound,
  currentUserId,
  refreshKey,
  isFinalRound,
  allMatchesCompleted,
  isAdmin,
  onUserSelect,
}) => {
  const { data: standings, loading, error, executeRequest } = useApiState([]);
  const { handleSort, sortedData: sortedStandings, getSortIcon } = useSortableTable(
    'season_total',
    'desc',
    standings,
    'display_name'
  );

  const showTrophies = isFinalRound && allMatchesCompleted;

  useEffect(() => {
    const fetchStandings = async () => {
      if (!currentRound) return;

      try {
        await executeRequest(async () => {
          const response = await leaderboardAPI.getRoundStandings(currentRound);
          return response.data.standings || [];
        });
      } catch (err) {
        console.error('Failed to fetch standings:', err);
      }
    };

    fetchStandings();
  }, [currentRound, refreshKey, executeRequest]);

  const isRankTied = (standings, index) => {
    const currentUser = standings[index];
    // Check if previous user has same season total
    const prevTied = index > 0 && standings[index - 1].season_total === currentUser.season_total;
    // Check if next user has same season total
    const nextTied =
      index < standings.length - 1 &&
      standings[index + 1].season_total === currentUser.season_total;
    return prevTied || nextTied;
  };

  const getRankDisplay = (rank, isTied) => {
    // Use a fixed-width approach: always include the prefix, but make it invisible when not tied
    const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : '';
    const prefix = isTied ? '=' : '';

    return (
      <>
        {medal}
        <span style={{ display: 'inline-block', minWidth: '0.5em', textAlign: 'center' }}>
          {prefix}
        </span>
        {rank}
      </>
    );
  };

  const getRankClass = (rank) => {
    if (rank === 1) return 'rank-gold';
    if (rank === 2) return 'rank-silver';
    if (rank === 3) return 'rank-bronze';
    return '';
  };

  const getTrophy = (rank) => {
    if (!showTrophies) return '';
    if (rank === 1) return ' 🏆';
    if (rank === 2) return ' 🥈';
    if (rank === 3) return ' 🥉';
    return '';
  };

  if (loading) {
    return (
      <div className="round-standings">
        <div className="standings-header">
          <h3>Standings</h3>
        </div>
        <div className="standings-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="round-standings">
        <div className="standings-header">
          <h3>Standings</h3>
        </div>
        <div className="standings-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="round-standings">
      <div className="standings-header">
        <h3>Standings</h3>
      </div>
      <div className="standings-list">
        {standings.length === 0 ? (
          <div className="standings-empty">No standings available yet</div>
        ) : (
          <>
            {/* Column Headers */}
            <div className="standings-header-row">
              <div className="header-rank sortable" onClick={() => handleSort('rank')}>
                Rank {getSortIcon('rank')}
              </div>
              <div className="header-team"></div>
              <div className="header-name sortable" onClick={() => handleSort('display_name')}>
                Player {getSortIcon('display_name')}
              </div>
              <div className="header-picks sortable" onClick={() => handleSort('picks_made')}>
                P {getSortIcon('picks_made')}
              </div>
              <div className="header-round sortable" onClick={() => handleSort('round_points')}>
                R# {getSortIcon('round_points')}
              </div>
              <div className="header-season sortable" onClick={() => handleSort('season_total')}>
                S# {getSortIcon('season_total')}
              </div>
            </div>

            {/* Data Rows */}
            {sortedStandings.map((user, index) => {
              const tied = isRankTied(sortedStandings, index);
              const isClickable = isAdmin && onUserSelect;
              return (
                <div
                  key={user.user_id}
                  className={`standings-row ${
                    user.user_id === currentUserId ? 'current-user' : ''
                  } ${getRankClass(user.rank)} ${isClickable ? 'admin-clickable' : ''}`}
                  onClick={isClickable ? () => onUserSelect(user.user_id) : undefined}
                  title={isClickable ? `View ${user.display_name || 'user'}'s picks` : undefined}
                >
                  <div className="rank-badge">
                    <span className="rank-value">{getRankDisplay(user.rank, tied)}</span>
                  </div>
                  <div className="team-flag-cell">
                    {user.favorite_team_flag && (
                      <img
                        src={`/assets/team-flags/${user.favorite_team_flag}`}
                        alt="Favorite team"
                        className="team-flag-icon"
                        title={user.favorite_team_name || 'Favorite team'}
                      />
                    )}
                  </div>
                  <div className="user-info">
                    <div className="user-name">
                      {user.display_name || `User ${user.user_id}`}
                      {getTrophy(user.rank)}
                      {user.user_id === currentUserId && (
                        <span className="you-indicator">(You)</span>
                      )}
                    </div>
                  </div>
                  <div
                    className={`picks-indicator ${user.picks_made === user.total_matches ? 'complete' : 'incomplete'}`}
                  >
                    {user.picks_made === user.total_matches ? '✓' : '✗'}
                  </div>
                  <div className="score-value">{user.round_points}</div>
                  <div className="score-value">{user.season_total}</div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

// P-6 FIX: Wrap with React.memo to prevent unnecessary re-renders
// Component only re-renders when props (currentRound, currentUserId, refreshKey, etc.) change
export default React.memo(RoundStandings);
